import { useState, useEffect, useRef, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Play, 
  Save, 
  RotateCcw, 
  Clock, 
  CheckCircle, 
  XCircle,
  Code2,
  FileText,
  Target,
  ChevronLeft,
  ChevronRight,
  Settings,
  Eye,
  EyeOff,
  AlertCircle,
  Terminal,
  ChevronDown,
  ChevronUp,
  Database,
  TableProperties
} from 'lucide-react'
import Editor from '@monaco-editor/react'
import { useStageState } from '@/hooks/useStageState'
import { useNavigationWithSave } from '@/hooks/useNavigationWithSave'
import { useInterviewStore } from '@/store/interviewStore'
import { buildDatabaseExecutionApiUrl } from '@/config/api'
import { apiClient } from '@/lib/api'
import { useDatabaseAutoSave } from '@/hooks/useDatabaseAutoSave'

// Available database types
const DATABASE_TYPES = [
  { value: 'sqlite', label: 'SQLite', icon: '💎' },
  { value: 'mysql', label: 'MySQL', icon: '🐬' },
  { value: 'sqlserver', label: 'SQL Server', icon: '🏢' },
  // { value: 'postgresql', label: 'PostgreSQL', icon: '🐘' }
] as const

interface DatabaseChallenge {
  assignment_uuid: string
  challenge_uuid: string
  title: string
  description: string
  difficulty: 'Easy' | 'Medium' | 'Hard'
  challenge_type: string
  database_type: string
  schema_setup: string
  expected_result?: any
  hints: string[]
  time_limit_minutes: number
  assignment_order: number
  submission_uuid?: string
  draft_query?: string
  submitted_query?: string
  execution_result?: any
  execution_error?: string
  is_correct?: boolean
  score?: number
  submission_status?: string
  submitted_at?: string
  time_taken_seconds?: number | null
  save_type?: string
  ai_assistance_used?: boolean
  ai_queries_count?: number
  ai_tokens_used?: number
  last_saved_at?: string | null
  last_updated_at?: string | null
  manual_feedback?: string | null
  submission?: {
    submitted_query?: string
    execution_result?: any
    execution_error?: string
    is_correct?: boolean
    score?: number
    submission_status?: string
    submitted_at?: string | null
    manual_feedback?: string | null
    time_taken_seconds?: number | null
    save_type?: string
    ai_assistance_used?: boolean
    ai_queries_count?: number
    ai_tokens_used?: number
    last_saved_at?: string | null
    last_updated_at?: string | null
    version_number?: number
    is_completed?: boolean
  }
}

interface DatabaseResultTable {
  columns?: any[]
  rows?: any[][]
}

interface DatabaseValidationComparisonCheck {
  type: string
  message: string
  expected?: any
  actual?: any
}

interface DatabaseValidationComparisonDetails {
  successful_checks: DatabaseValidationComparisonCheck[]
  failed_checks: DatabaseValidationComparisonCheck[]
  total_checks: number
  success_count: number
  failure_count: number
}

interface DatabaseValidationResult {
  is_correct: boolean
  score: number
  feedback?: string
  differences?: Record<string, any>
  difference_messages?: string[]
  user_result?: DatabaseResultTable & Record<string, any>
  expected_result?: DatabaseResultTable & Record<string, any>
  execution_time_ms?: number
  error?: string
  timestamp?: string
  comparison_details?: DatabaseValidationComparisonDetails
}

const isArrayShallowEqual = (first: any[] = [], second: any[] = []): boolean => {
  if (first.length !== second.length) return false
  for (let i = 0; i < first.length; i += 1) {
    if (first[i] !== second[i]) return false
  }
  return true
}

const stringifyRowKey = (row: any): string => {
  return JSON.stringify(row, (_key, value) => {
    if (typeof value === 'bigint') return value.toString()
    if (value === undefined) return '__undefined__'
    return value
  })
}

const createRowCounter = (rows: any[][] = []) => {
  const counter = new Map<string, { count: number; row: any[] }>()
  rows.forEach((row) => {
    const key = stringifyRowKey(row)
    const entry = counter.get(key)
    if (entry) {
      entry.count += 1
    } else {
      counter.set(key, { count: 1, row: Array.isArray(row) ? [...row] : row })
    }
  })
  return counter
}

const cloneRow = (row: any): any => {
  if (Array.isArray(row)) return row.map(cloneRow)
  if (row && typeof row === 'object') return JSON.parse(JSON.stringify(row))
  return row
}

const formatRowPreview = (rows: any[][], limit = 3): string => {
  if (!rows || rows.length === 0) return '[]'
  const previewRows = rows.slice(0, limit).map((row) => JSON.stringify(row))
  const suffix = rows.length > limit ? ', ...' : ''
  return `[${previewRows.join(', ')}${suffix}]`
}

const compareDatabaseResults = (
  expected: DatabaseResultTable | undefined,
  actual: DatabaseResultTable | undefined
): {
  isMatch: boolean
  feedback: string
  score: number
  differences: Record<string, any>
  differenceMessages: string[]
  comparisonDetails: DatabaseValidationComparisonDetails
} => {
  const expectedColumns = Array.isArray(expected?.columns) ? expected?.columns ?? [] : []
  const expectedRows = Array.isArray(expected?.rows) ? expected?.rows ?? [] : []
  const actualColumns = Array.isArray(actual?.columns) ? actual?.columns ?? [] : []
  const actualRows = Array.isArray(actual?.rows) ? actual?.rows ?? [] : []

  const differences: Record<string, any> = {}
  const differenceMessages: string[] = []
  const successfulChecks: DatabaseValidationComparisonCheck[] = []
  const failedChecks: DatabaseValidationComparisonCheck[] = []

  // Column comparison
  if (isArrayShallowEqual(expectedColumns, actualColumns)) {
    successfulChecks.push({
      type: 'columns',
      message: `Columns match: ${JSON.stringify(expectedColumns)}`,
      expected: expectedColumns,
      actual: actualColumns
    })
  } else {
    const missingColumns = expectedColumns.filter((column) => !actualColumns.includes(column))
    const extraColumns = actualColumns.filter((column) => !expectedColumns.includes(column))

    if (missingColumns.length > 0) {
      differences.missing_columns = missingColumns
      failedChecks.push({
        type: 'missing_columns',
        message: `Missing columns: ${missingColumns.join(', ')}`,
        expected: missingColumns,
        actual: []
      })
      differenceMessages.push(`Missing columns: ${missingColumns.join(', ')}`)
    }

    if (extraColumns.length > 0) {
      differences.unexpected_columns = extraColumns
      failedChecks.push({
        type: 'unexpected_columns',
        message: `Unexpected columns: ${extraColumns.join(', ')}`,
        expected: [],
        actual: extraColumns
      })
      differenceMessages.push(`Unexpected columns: ${extraColumns.join(', ')}`)
    }

    if (missingColumns.length === 0 && extraColumns.length === 0 && !isArrayShallowEqual(expectedColumns, actualColumns)) {
      differences.column_order = {
        expected: expectedColumns,
        actual: actualColumns
      }
      failedChecks.push({
        type: 'column_order',
        message: 'Column order differs from the expected output',
        expected: expectedColumns,
        actual: actualColumns
      })
      differenceMessages.push('Column order differs from the expected output')
    }
  }

  // Row count comparison
  if (expectedRows.length === actualRows.length) {
    successfulChecks.push({
      type: 'row_count',
      message: `Row count matches: ${expectedRows.length} rows`,
      expected: expectedRows.length,
      actual: actualRows.length
    })
  } else {
    differences.row_count = {
      expected: expectedRows.length,
      actual: actualRows.length
    }
    failedChecks.push({
      type: 'row_count',
      message: `Expected ${expectedRows.length} rows but got ${actualRows.length}`,
      expected: expectedRows.length,
      actual: actualRows.length
    })
    differenceMessages.push(`Expected ${expectedRows.length} rows but got ${actualRows.length}`)
  }

  // Row content comparison
  if (expectedRows.length > 0 || actualRows.length > 0) {
    const expectedCounter = createRowCounter(expectedRows)
    const actualCounter = createRowCounter(actualRows)

    const missingRows: any[][] = []
    const extraRows: any[][] = []

    expectedCounter.forEach((expectedEntry, key) => {
      const actualEntry = actualCounter.get(key)
      const actualCount = actualEntry?.count ?? 0
      if (actualCount < expectedEntry.count) {
        const deficit = expectedEntry.count - actualCount
        for (let i = 0; i < deficit; i += 1) {
          missingRows.push(Array.isArray(expectedEntry.row) ? expectedEntry.row.map(cloneRow) : cloneRow(expectedEntry.row))
        }
      }
    })

    actualCounter.forEach((actualEntry, key) => {
      const expectedEntry = expectedCounter.get(key)
      const expectedCount = expectedEntry?.count ?? 0
      if (actualEntry.count > expectedCount) {
        const surplus = actualEntry.count - expectedCount
        for (let i = 0; i < surplus; i += 1) {
          extraRows.push(Array.isArray(actualEntry.row) ? actualEntry.row.map(cloneRow) : cloneRow(actualEntry.row))
        }
      }
    })

    if (missingRows.length > 0) {
      differences.missing_rows = missingRows
      failedChecks.push({
        type: 'missing_rows',
        message: `Missing rows: ${formatRowPreview(missingRows)}`,
        expected: missingRows,
        actual: []
      })
      differenceMessages.push(`Missing rows: ${formatRowPreview(missingRows)}`)
    }

    if (extraRows.length > 0) {
      differences.unexpected_rows = extraRows
      failedChecks.push({
        type: 'unexpected_rows',
        message: `Unexpected rows: ${formatRowPreview(extraRows)}`,
        expected: [],
        actual: extraRows
      })
      differenceMessages.push(`Unexpected rows: ${formatRowPreview(extraRows)}`)
    }

    const noMissingOrExtra = missingRows.length === 0 && extraRows.length === 0
    if (noMissingOrExtra) {
      if (JSON.stringify(expectedRows) === JSON.stringify(actualRows)) {
        successfulChecks.push({
          type: 'row_content',
          message: 'All row content matches exactly',
          expected: expectedRows,
          actual: actualRows
        })
      } else if (expectedRows.length === actualRows.length) {
        differences.row_order = {
          expected: expectedRows,
          actual: actualRows
        }
        failedChecks.push({
          type: 'row_order',
          message: 'Row order differs from the expected output',
          expected: expectedRows,
          actual: actualRows
        })
        differenceMessages.push('Row order differs from the expected output')
      }
    }
  }

  const totalChecks = successfulChecks.length + failedChecks.length
  const successCount = successfulChecks.length
  const failureCount = failedChecks.length
  const isMatch = failureCount === 0 && totalChecks > 0
  const comparisonDetails: DatabaseValidationComparisonDetails = {
    successful_checks: successfulChecks,
    failed_checks: failedChecks,
    total_checks: totalChecks,
    success_count: successCount,
    failure_count: failureCount
  }

  let score = 0
  if (totalChecks > 0) {
    score = Math.round((successCount / totalChecks) * 100)
  } else if (isMatch) {
    score = 100
  }

  let feedback = 'No expected result available for comparison.'
  if (isMatch) {
    feedback = 'Test passed: query output matches the expected result.'
  } else if (successCount > 0 && failureCount > 0) {
    feedback = differenceMessages.length > 0
      ? `Partial match: ${differenceMessages.join('; ')}`
      : 'Partial match: some checks passed while others failed.'
  } else if (failureCount > 0) {
    feedback = differenceMessages.length > 0
      ? `Test failed: ${differenceMessages.join('; ')}`
      : 'Test failed: output does not match the expected result.'
  }

  return {
    isMatch,
    feedback,
    score,
    differences,
    differenceMessages,
    comparisonDetails
  }
}

const parseJsonField = (value: any) => {
  if (!value) return value
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch (_error) {
      return value
    }
  }
  return value
}

const parseBoolean = (value: any, defaultValue = false): boolean => {
  if (value === undefined || value === null) return defaultValue
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) return true
    if (['false', '0', 'no', 'n', 'off'].includes(normalized)) return false
  }
  return defaultValue
}

const parseNumber = (value: any): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const normalizeDate = (value: any): string | null => {
  if (!value) return null
  if (typeof value === 'string') return value
  try {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
      return null
    }
    return date.toISOString()
  } catch (_error) {
    return null
  }
}

export default function DatabaseQuestions() {
  const store = useInterviewStore()
  const { activeProblemId, switchDatabaseProblem } = useNavigationWithSave()
  
  // Database challenges state
  const [databaseChallenges, setDatabaseChallenges] = useState<DatabaseChallenge[]>([])
  const [isLoadingChallenges, setIsLoadingChallenges] = useState(true)
  const [currentChallengeId, setCurrentChallengeId] = useState<string>('')
  
  // Component state
  const [testResults, setTestResults] = useState<any[]>([])
  const [output, setOutput] = useState('')
  const [isRunning, setIsRunning] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [executionResult, setExecutionResult] = useState<any>(null)
  const [validationResult, setValidationResult] = useState<DatabaseValidationResult | null>(null)
  const [leftPanelWidth, setLeftPanelWidth] = useState(50)
  const [selectedDatabaseType, setSelectedDatabaseType] = useState<string>('sqlite')
  const [resultsHeight, setResultsHeight] = useState(400) // Height of results panel in pixels
  const [isResizing, setIsResizing] = useState(false)
  const [problemPanelCollapsed, setProblemPanelCollapsed] = useState(false)
  const [challengesLoaded, setChallengesLoaded] = useState(false)
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false)
  const [submissionState, setSubmissionState] = useState<Record<string, { status: string; submittedAt: string | null }>>({})
  
  // Collapse states for different sections
  const [schemaCollapsed, setSchemaCollapsed] = useState(false)
  const [hintsCollapsed, setHintsCollapsed] = useState(false)
  const [expectedResultCollapsed, setExpectedResultCollapsed] = useState(false)
  
  // Editor ref for accessing Monaco editor instance
  const editorRef = useRef<any>(null)

  // Handle resize functionality
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
    
    const startY = e.clientY
    const startHeight = resultsHeight
    
    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = startY - e.clientY // Inverted because we're resizing from top
      const newHeight = Math.max(200, Math.min(800, startHeight + deltaY)) // Min 200px, max 800px
      setResultsHeight(newHeight)
    }
    
    const handleMouseUp = () => {
      setIsResizing(false)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
    
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleSelectChallenge = (assignmentUuid: string) => {
    setCurrentChallengeId(assignmentUuid)
    void switchDatabaseProblem(assignmentUuid)
    setShowSubmitConfirm(false)
  }

  
  // Maintain latest navigation handler for use inside effects
  const switchDatabaseProblemRef = useRef(switchDatabaseProblem)
  useEffect(() => {
    switchDatabaseProblemRef.current = switchDatabaseProblem
  }, [switchDatabaseProblem])

  // Load database challenges for this interview session
  useEffect(() => {
    const loadChallenges = async () => {
      try {
        setIsLoadingChallenges(true)
        setChallengesLoaded(false)

        const sessionId = store.sessionId
        const interviewId = store.interviewId

        if (!sessionId || !interviewId) {
          setDatabaseChallenges([])
          setCurrentChallengeId('')
          return
        }

        console.log('🔍 Loading database challenges for interview:', interviewId, 'session:', sessionId)
        const response = await apiClient.getInterviewDatabaseChallenges(interviewId, sessionId)

        if (response.success && Array.isArray(response.data) && response.data.length > 0) {
          const normalizedChallenges: DatabaseChallenge[] = (response.data as any[]).map((rawChallenge: any) => {
            const submission = rawChallenge.submission || {}
            const expectedResult = parseJsonField(rawChallenge.expected_result)
            const hintsParsed = parseJsonField(rawChallenge.hints)
            const executionResultParsed = parseJsonField(rawChallenge.execution_result ?? submission.execution_result)
            const normalizedExecutionResult = executionResultParsed && typeof executionResultParsed === 'object'
              ? executionResultParsed
              : null
            const submittedQuery = rawChallenge.submitted_query
              ?? rawChallenge.draft_query
              ?? submission.submitted_query
              ?? submission.draft_query
              ?? ''
            const draftQuery = rawChallenge.draft_query ?? submission.draft_query ?? submittedQuery
            const scoreValue = parseNumber(rawChallenge.score ?? submission.score)
            const timeTaken = parseNumber(rawChallenge.time_taken_seconds ?? submission.time_taken_seconds)
            const aiQueries = parseNumber(rawChallenge.ai_queries_count ?? submission.ai_queries_count) ?? 0
            const aiTokens = parseNumber(rawChallenge.ai_tokens_used ?? submission.ai_tokens_used) ?? 0
            const aiAssistance = parseBoolean(rawChallenge.ai_assistance_used ?? submission.ai_assistance_used, false)
            const submissionStatus = (rawChallenge.submission_status ?? submission.submission_status ?? 'not_started') as string
            const savedAt = normalizeDate(rawChallenge.last_saved_at ?? submission.last_saved_at ?? rawChallenge.last_save_at ?? submission.last_save_at)
            const lastUpdatedAt = normalizeDate(rawChallenge.last_updated_at ?? submission.last_updated_at)
            const submittedAt = normalizeDate(rawChallenge.submitted_at ?? submission.submitted_at)
            const manualFeedback = rawChallenge.manual_feedback ?? submission.manual_feedback ?? null
            const executionError = rawChallenge.execution_error ?? submission.execution_error ?? null
            const isCorrect = parseBoolean(rawChallenge.is_correct ?? submission.is_correct, false)
            const saveType = rawChallenge.save_type ?? submission.save_type ?? 'auto'

            const normalizedSubmission = {
              ...submission,
              submitted_query: submittedQuery,
              execution_result: normalizedExecutionResult,
              execution_error: executionError,
              is_correct: isCorrect,
              score: scoreValue,
              submission_status: submissionStatus,
              submitted_at: submittedAt ?? null,
              manual_feedback: manualFeedback,
              time_taken_seconds: timeTaken ?? null,
              save_type: saveType,
              ai_assistance_used: aiAssistance,
              ai_queries_count: aiQueries,
              ai_tokens_used: aiTokens,
              last_saved_at: savedAt,
              last_updated_at: lastUpdatedAt,
            }

            return {
              ...rawChallenge,
              expected_result: expectedResult ?? rawChallenge.expected_result,
              hints: Array.isArray(hintsParsed) ? hintsParsed : [],
              submission: normalizedSubmission,
              submission_uuid: rawChallenge.submission_uuid ?? submission.submission_uuid ?? rawChallenge.assignment_uuid,
              draft_query: draftQuery,
              submitted_query: submittedQuery,
              execution_result: normalizedExecutionResult,
              execution_error: executionError,
              is_correct: isCorrect,
              score: scoreValue,
              submission_status: submissionStatus,
              submitted_at: submittedAt ?? undefined,
              manual_feedback: manualFeedback,
              time_taken_seconds: timeTaken ?? null,
              save_type: saveType,
              ai_assistance_used: aiAssistance,
              ai_queries_count: aiQueries,
              ai_tokens_used: aiTokens,
              last_saved_at: savedAt,
              last_updated_at: lastUpdatedAt,
            }
          })

          setDatabaseChallenges(normalizedChallenges)
          const statusMap = normalizedChallenges.reduce<Record<string, { status: string; submittedAt: string | null }>>((acc, ch) => {
            const key = ch.assignment_uuid || ch.uuid || ch.id
            if (key) {
              acc[key] = {
                status: (ch.submission_status || 'not_started').toLowerCase(),
                submittedAt: ch.submitted_at || null
              }
            }
            return acc
          }, {})
          setSubmissionState(statusMap)
          console.log('📚 Normalized database challenges:', normalizedChallenges)
        } else {
          console.log('💻 No database challenges found')
          setDatabaseChallenges([])
          setCurrentChallengeId('')
        }
      } catch (error) {
        console.error('❌ Error loading challenges:', error)
        setDatabaseChallenges([])
        setCurrentChallengeId('')
      } finally {
        setIsLoadingChallenges(false)
        setChallengesLoaded(true)
      }
    }

    loadChallenges()
  }, [store.sessionId, store.interviewId])

  // Sync current challenge selection with loaded data and navigation store
  useEffect(() => {
    if (!databaseChallenges || databaseChallenges.length === 0) {
      return
    }

    const activeId = useInterviewStore.getState().activeProblemId
    const hasActive = activeId && databaseChallenges.some(challenge => challenge.assignment_uuid === activeId)
    const fallbackId = databaseChallenges[0]?.assignment_uuid
    const targetId = (hasActive ? activeId : fallbackId) || ''

    if (!targetId || targetId === currentChallengeId) {
      return
    }

    setCurrentChallengeId(targetId)
    const switchFn = switchDatabaseProblemRef.current
    if (switchFn) {
      void switchFn(targetId)
    }
  }, [databaseChallenges, currentChallengeId])

  useEffect(() => {
    if (!activeProblemId) return
    if (!databaseChallenges || databaseChallenges.length === 0) return
    if (currentChallengeId === activeProblemId) return
    const exists = databaseChallenges.some(challenge => challenge.assignment_uuid === activeProblemId)
    if (!exists) return
    setCurrentChallengeId(activeProblemId)
  }, [activeProblemId, currentChallengeId, databaseChallenges])

  // Current challenge data - use activeProblemId from store if available
  const currentChallenge = databaseChallenges.find(c => c.assignment_uuid === (activeProblemId || currentChallengeId)) || databaseChallenges[0]

  // Initialize selected database type when challenge loads
  useEffect(() => {
    if (currentChallenge?.database_type) {
      setSelectedDatabaseType(currentChallenge.database_type)
    }
  }, [currentChallenge?.database_type])

  // Use hooks for current challenge - use assignment_uuid as problemId
  const currentProblemId = currentChallenge?.assignment_uuid || ''
  const { data, updateData } = useStageState('database-questions', currentProblemId)

  // Hydrate editor with previously saved query/execution state when available
  useEffect(() => {
    if (!challengesLoaded || !currentChallenge) return

    const savedQuery = (currentChallenge.submitted_query ?? currentChallenge.draft_query ?? '').trim()

    if (savedQuery.length > 0 && (!data?.query || data.query.trim().length === 0)) {
      updateData(currentChallenge.submitted_query ?? currentChallenge.draft_query ?? '')
      store.markDatabaseClean(currentProblemId)
    }
  }, [challengesLoaded, currentChallenge?.assignment_uuid, currentProblemId, data?.query, store, updateData])

  useEffect(() => {
    if (!challengesLoaded || !currentChallenge) return

    // Clear results on page reload/challenge change - don't show previous execution results
    setExecutionResult(null)
    setValidationResult(null)
    setTestResults([])
    setOutput('')
    setShowSubmitConfirm(false)
  }, [challengesLoaded, currentChallenge?.assignment_uuid])

  const latestExecutionError = executionResult?.success === false
    ? executionResult?.error || executionResult?.message || null
    : null

  const latestTimeTakenSeconds = executionResult?.execution_time_ms
    ? Math.round(executionResult.execution_time_ms / 1000)
    : undefined

  const { saveNow: saveDatabaseProgress } = useDatabaseAutoSave({
    assignmentUuid: currentChallenge?.assignment_uuid,
    problemId: currentProblemId,
    sessionId: store.sessionId,
    query: data?.query || '',
    executionResult,
    executionError: latestExecutionError,
    timeTakenSeconds: latestTimeTakenSeconds,
  })

  const submissionStatus = (submissionState[currentProblemId]?.status || currentChallenge?.submission_status || currentChallenge?.submission?.submission_status || '').toLowerCase()
  const submittedAt = submissionState[currentProblemId]?.submittedAt || currentChallenge?.submitted_at || currentChallenge?.submission?.submitted_at || null
  const isSubmissionLocked = submissionStatus === 'submitted' || submissionStatus === 'evaluated'

  const displayedValidationResult = useMemo<DatabaseValidationResult | null>(() => {
    if (!validationResult) return null

    if (validationResult.comparison_details && validationResult.comparison_details.total_checks > 0) {
      return validationResult
    }

    if (validationResult.expected_result && validationResult.user_result) {
      const comparison = compareDatabaseResults(validationResult.expected_result, validationResult.user_result)
      return {
        ...validationResult,
        comparison_details: comparison.comparisonDetails,
        differences: Object.keys(validationResult.differences ?? {}).length > 0
          ? validationResult.differences
          : comparison.differences,
        difference_messages: (validationResult.difference_messages && validationResult.difference_messages.length > 0)
          ? validationResult.difference_messages
          : comparison.differenceMessages,
        score: typeof validationResult.score === 'number' && validationResult.score > 0
          ? validationResult.score
          : comparison.score,
        feedback: validationResult.feedback || comparison.feedback,
        is_correct: validationResult.is_correct ?? comparison.isMatch
      }
    }

    return validationResult
  }, [validationResult])

  const validationStatus = useMemo(() => {
    if (!displayedValidationResult) {
      return null
    }

    const comparisonDetails = displayedValidationResult.comparison_details
    const hasPartialMatch = !!comparisonDetails && comparisonDetails.success_count > 0 && comparisonDetails.failure_count > 0

    if (displayedValidationResult.is_correct) {
      return {
        label: 'Test Passed',
        badgeClass: 'bg-green-100 text-green-800',
        icon: CheckCircle,
        iconClass: 'text-green-600',
        statusText: 'Passed',
        statusClass: 'text-green-600',
        tone: 'success'
      }
    }

    if (hasPartialMatch) {
      return {
        label: 'Partial Match',
        badgeClass: 'bg-yellow-100 text-yellow-800',
        icon: AlertCircle,
        iconClass: 'text-yellow-600',
        statusText: 'Partial',
        statusClass: 'text-yellow-700',
        tone: 'warning'
      }
    }

    return {
      label: 'Test Failed',
      badgeClass: 'bg-red-100 text-red-800',
      icon: XCircle,
      iconClass: 'text-red-600',
      statusText: 'Failed',
      statusClass: 'text-red-600',
      tone: 'error'
    }
  }, [displayedValidationResult])
  
  // Handle executing SQL query
  const handleExecuteQuery = async () => {
    if (!data || !data.query || !currentChallenge) return
    
    const queryStartTime = Date.now()
    setIsRunning(true)
    setOutput('Executing query...')
    setExecutionResult(null)
    
    try {
      console.log('🔍 Executing database query:', {
        database_type: selectedDatabaseType,
        query: data.query,
        schema_setup: currentChallenge.schema_setup,
        start_time: new Date(queryStartTime).toISOString()
      })

      const sessionId = store.sessionId

      // Use AWS API Gateway for database execution (like coding challenges)
      let apiUrl = buildDatabaseExecutionApiUrl('database/public/execute', 'POST')
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'X-API-Key': 'test-api-key-123',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          database_type: selectedDatabaseType,
          query: data.query.trim(),
          schema_setup: currentChallenge.schema_setup || '',
          session_id: sessionId,
          challenge_id: currentChallenge.challenge_uuid,
          query_start_time: new Date(queryStartTime).toISOString()
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Database execution failed: ${response.status} - ${errorText}`)
      }

      const result = await response.json()
      const queryEndTime = Date.now()
      const totalQueryDuration = queryEndTime - queryStartTime
      
      console.log('🔍 Database execution result:', {
        ...result,
        client_timing: {
          start_time: new Date(queryStartTime).toISOString(),
          end_time: new Date(queryEndTime).toISOString(),
          total_duration_ms: totalQueryDuration
        }
      })

      if (result.success && result.data) {
        const executionPayload = {
          success: true,
          rows: result.data.rows || [],
          columns: result.data.columns || [],
          affected_rows: result.data.affected_rows,
          execution_time_ms: result.data.execution_time_ms,
          query_type: result.data.query_type,
          database_type: result.data.database_type,
          session_id: result.data.session_id,
          execution_id: result.data.execution_id,
          message: result.data.message,
          timestamp: result.data.timestamp,
          container_ip: result.data.container_ip,
          database: result.data.database
        }
        setExecutionResult(executionPayload)

        const execMs = typeof result.data.execution_time_ms === 'number'
          ? Math.round(result.data.execution_time_ms)
          : undefined
        const executionTime = execMs !== undefined ? ` (${execMs}ms)` : ''
        const rowCount = result.data.rows ? result.data.rows.length : 0
        const baseMessage = `Query executed successfully! ${rowCount} rows returned${executionTime}`

        let statusMessage = baseMessage
        const expected = currentChallenge.expected_result
        if (expected && typeof expected === 'object') {
          const comparison = compareDatabaseResults(expected, {
            columns: executionPayload.columns,
            rows: executionPayload.rows
          })

          const derivedValidation: DatabaseValidationResult = {
            is_correct: comparison.isMatch,
            score: comparison.score,
            feedback: comparison.feedback,
            differences: comparison.differences,
            difference_messages: comparison.differenceMessages,
            expected_result: expected,
            user_result: {
              columns: executionPayload.columns,
              rows: executionPayload.rows,
              execution_time_ms: executionPayload.execution_time_ms,
              affected_rows: executionPayload.affected_rows,
              query_type: executionPayload.query_type,
              database_type: executionPayload.database_type
            },
            execution_time_ms: executionPayload.execution_time_ms,
            comparison_details: comparison.comparisonDetails
          }
          setValidationResult(derivedValidation)

          const hasPartialMatch = comparison.comparisonDetails.success_count > 0 && comparison.comparisonDetails.failure_count > 0
          if (comparison.isMatch) {
            statusMessage = `${baseMessage}\n✅ Test Passed: query output matches the expected result.`
          } else if (hasPartialMatch) {
            const detailMessage = comparison.differenceMessages.join('; ') || 'Some checks passed while others failed.'
            statusMessage = `${baseMessage}\n⚠️ Partial Match: ${detailMessage}`
          } else {
            const detailMessage = comparison.differenceMessages.join('; ') || 'Output does not match the expected result.'
            statusMessage = `${baseMessage}\n❌ Test Failed: ${detailMessage}`
          }
        } else {
          setValidationResult(null)
        }

        setOutput(statusMessage)

        await saveDatabaseProgress({
          query: data?.query?.trim() || '',
          executionResult: executionPayload,
          executionError: null,
          timeTakenSeconds: executionPayload.execution_time_ms
            ? Math.round(executionPayload.execution_time_ms / 1000)
            : undefined,
          saveType: 'manual'
        })
      } else {
        const errorMsg = result.data?.error || result.error || 'Unknown error occurred'
        const failurePayload = {
          success: false,
          error: errorMsg
        }
        setExecutionResult(failurePayload)
        setOutput(`Query execution failed: ${errorMsg}`)

        await saveDatabaseProgress({
          query: data?.query?.trim() || '',
          executionResult: failurePayload,
          executionError: errorMsg,
          saveType: 'manual'
        })
      }
      
    } catch (error) {
      console.error('Database execution error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      const failurePayload = {
        success: false,
        error: errorMessage
      }
      setExecutionResult(failurePayload)
      setOutput(`Error: ${errorMessage}`)
      await saveDatabaseProgress({
        query: data?.query?.trim() || '',
        executionResult: failurePayload,
        executionError: errorMessage,
        saveType: 'manual'
      })
    } finally {
      setIsRunning(false)
    }
  }

  // Handle running test cases
  const handleRunTests = async () => {
    if (!data || !data.query || !currentChallenge || !currentChallenge.expected_result) {
      setOutput('No test cases available for this challenge')
      return
    }
    
    setIsRunning(true)
    setOutput('Running test cases...')
    setValidationResult(null)
    
    try {
      const sessionId = store.sessionId
      console.log('🧪 Running database test cases...')
      
      let validationApiUrl = buildDatabaseExecutionApiUrl('database/public/validate-solution', 'POST')
      let validationResponse = await fetch(validationApiUrl, {
        method: 'POST',
        headers: {
          'X-API-Key': 'test-api-key-123',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          database_type: selectedDatabaseType,
          user_query: data.query.trim(),
          expected_result: currentChallenge.expected_result,
          session_id: sessionId,
          challenge_id: currentChallenge.challenge_uuid,
          schema_setup: currentChallenge.schema_setup || ''
        })
      })

      if (validationResponse.ok) {
        const validationData = await validationResponse.json()
        const apiResult = validationData.data as DatabaseValidationResult

        let mergedResult: DatabaseValidationResult = { ...apiResult }

        if (mergedResult.expected_result && mergedResult.user_result) {
          const comparison = compareDatabaseResults(mergedResult.expected_result, mergedResult.user_result)
          mergedResult = {
            ...mergedResult,
            comparison_details: mergedResult.comparison_details ?? comparison.comparisonDetails,
            differences: Object.keys(mergedResult.differences ?? {}).length > 0 ? mergedResult.differences : comparison.differences,
            difference_messages: (mergedResult.difference_messages && mergedResult.difference_messages.length > 0)
              ? mergedResult.difference_messages
              : comparison.differenceMessages,
            feedback: mergedResult.feedback || comparison.feedback,
            score: typeof mergedResult.score === 'number' && mergedResult.score > 0 ? mergedResult.score : comparison.score,
            is_correct: mergedResult.is_correct ?? comparison.isMatch
          }
        }

        setValidationResult(mergedResult)
        console.log('🧪 Test result:', mergedResult)

        const comparisonDetails = mergedResult.comparison_details
        const hasPartialMatch = !!comparisonDetails && comparisonDetails.success_count > 0 && comparisonDetails.failure_count > 0
        const feedbackMessage = mergedResult.feedback || 'Validation complete.'

        if (mergedResult.is_correct) {
          setOutput(`✅ Test Passed: ${feedbackMessage}`)
        } else if (hasPartialMatch) {
          setOutput(`⚠️ Partial Match: ${feedbackMessage}`)
        } else {
          setOutput(`❌ Test Failed: ${feedbackMessage}`)
        }
      } else {
        const errorText = await validationResponse.text()
        setOutput(`Test execution failed: ${errorText}`)
      }
    } catch (error) {
      console.error('Test execution error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      setOutput(`Test execution error: ${errorMessage}`)
    } finally {
      setIsRunning(false)
    }
  }

  // Handle submitting solution
  const handleSubmitSolution = async () => {
    if (!data || !data.query || !currentChallenge || isSubmissionLocked) return
    
    setIsSubmitting(true)
    
    try {
      const sessionId = store.sessionId

      // First validate the solution if we have expected results
      let validationResultData = validationResult
      if (currentChallenge.expected_result && !validationResultData) {
        console.log('🔍 Validating database solution...')
        
        let validationApiUrl = buildDatabaseExecutionApiUrl('database/public/validate-solution', 'POST')
        let validationResponse = await fetch(validationApiUrl, {
          method: 'POST',
          headers: {
            'X-API-Key': 'test-api-key-123',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            database_type: selectedDatabaseType,
            user_query: data.query.trim(),
            expected_result: currentChallenge.expected_result,
            session_id: sessionId,
            challenge_id: currentChallenge.challenge_uuid,
            schema_setup: currentChallenge.schema_setup || ''
          })
        })

        
        if (validationResponse.ok) {
          const validationData = await validationResponse.json()
          validationResultData = validationData.data
          setValidationResult(validationResultData)
          console.log('🔍 Validation result:', validationResultData)
        }
      }

      // Then submit via the interview API
      const response = await apiClient.submitDatabaseChallenge({
        assignment_uuid: currentChallenge.assignment_uuid,
        submitted_query: data.query.trim(),
        execution_result: executionResult,
        is_correct: validationResultData?.is_correct || false,
        score: validationResultData?.score || 0,
        time_taken_seconds: Math.round((executionResult?.execution_time_ms || 0) / 1000)
      })
      
      if (response.success) {
        let message = 'Solution submitted successfully!'
        if (validationResultData && validationResultData.feedback) {
          message += ` - ${validationResultData.feedback}`
        }
        setOutput(message)
        setShowSubmitConfirm(false)

        const submittedTimestamp = new Date().toISOString()
        setSubmissionState(prev => ({
          ...prev,
          [currentProblemId]: {
            status: 'submitted',
            submittedAt: submittedTimestamp
          }
        }))

        setDatabaseChallenges(prev => prev.map(challenge =>
          challenge.assignment_uuid === currentChallenge.assignment_uuid
            ? {
                ...challenge,
                submission_status: 'submitted',
                submitted_at: submittedTimestamp,
                submission: {
                  ...(challenge.submission || {}),
                  submission_status: 'submitted',
                  submitted_at: submittedTimestamp,
                  submitted_query: data.query.trim()
                }
              }
            : challenge
        ))

        // Reload challenges to get updated submission status
        // TODO: Refresh challenge data if needed
      } else {
        setOutput('Failed to submit solution')
      }
    } catch (error) {
      console.error('Submission error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      setOutput(`Submission error: ${errorMessage}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Show loading state while fetching challenges
  if (isLoadingChallenges) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading database challenges...</p>
        </div>
      </div>
    )
  }

  // Show message if no challenges found
  if (databaseChallenges.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Database Challenges</h3>
          <p className="text-gray-600">No database challenges have been assigned to this interview session.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col md:flex-row">
      {/* Challenge Selection and Details Panel - Collapsible */}
      <motion.div 
        className="border-r border-gray-200 flex flex-col overflow-hidden"
        initial={false}
        animate={{
          width: problemPanelCollapsed ? '48px' : `${Math.max(35, Math.min(80, leftPanelWidth))}%`
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{
          minWidth: problemPanelCollapsed ? '48px' : '350px' // Ensure minimum width when expanded
        }}
      >
        {/* Panel Header with Collapse Button */}
        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <motion.button
                onClick={() => {
                  if (problemPanelCollapsed && leftPanelWidth < 35) {
                    // If expanding and current width is too small, set to reasonable default
                    setLeftPanelWidth(45)
                  }
                  setProblemPanelCollapsed(!problemPanelCollapsed)
                }}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.div
                  animate={{ rotate: problemPanelCollapsed ? 0 : 180 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <ChevronRight className="w-4 h-4" />
                </motion.div>
              </motion.button>
              <AnimatePresence>
                {!problemPanelCollapsed && (
                  <motion.div
                    className="flex items-center space-x-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2, delay: 0.1 }}
                  >
                    <Database className="w-6 h-6 text-blue-600" />
                    <h2 className="text-lg font-semibold text-gray-900 whitespace-nowrap">Database Challenge</h2>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Challenge Selector */}
        <AnimatePresence>
          {!problemPanelCollapsed && databaseChallenges.length > 1 && (
            <motion.div
              className="bg-white border-b border-gray-200 p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, delay: 0.1 }}
            >
              <h3 className="text-sm font-medium text-gray-900 mb-2">Database Challenges</h3>
              <div className="flex space-x-2">
                {databaseChallenges.map((challenge, index) => (
                  <button
                    key={challenge.assignment_uuid}
                    onClick={() => handleSelectChallenge(challenge.assignment_uuid)}
                    className={`px-3 py-1.5 text-sm rounded ${
                      challenge.assignment_uuid === currentChallengeId
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Challenge {index + 1}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Challenge Details */}
        <AnimatePresence>
          {!problemPanelCollapsed && currentChallenge && (
            <motion.div
              className="flex-1 overflow-auto p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, delay: 0.1 }}
            >
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-gray-900">
                  {currentChallenge.title}
                </h2>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  currentChallenge.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                  currentChallenge.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {currentChallenge.difficulty}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-4">{currentChallenge.description}</p>
            </div>
            
            {/* Database Schema */}
            <div className="mb-6">
              <button
                onClick={() => setSchemaCollapsed(!schemaCollapsed)}
                className="w-full flex items-center justify-between text-sm font-medium text-gray-900 mb-2 hover:text-blue-600 transition-colors"
              >
                <div className="flex items-center">
                  <TableProperties className="w-4 h-4 mr-1" />
                  Database Schema
                </div>
                <motion.div
                  animate={{ rotate: schemaCollapsed ? -90 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown className="w-4 h-4" />
                </motion.div>
              </button>
              <AnimatePresence>
                {!schemaCollapsed && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-60 font-mono">
                      {currentChallenge.schema_setup}
                    </pre>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Hints */}
            {currentChallenge.hints && currentChallenge.hints.length > 0 && (
              <div className="mb-6">
                <button
                  onClick={() => setHintsCollapsed(!hintsCollapsed)}
                  className="w-full flex items-center justify-between text-sm font-medium text-gray-900 mb-2 hover:text-blue-600 transition-colors"
                >
                  <span>Hints ({currentChallenge.hints.length})</span>
                  <motion.div
                    animate={{ rotate: hintsCollapsed ? -90 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {!hintsCollapsed && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <ul className="text-sm text-gray-600 space-y-2">
                        {currentChallenge.hints.map((hint, index) => (
                          <li key={index} className="flex items-start">
                            <span className="text-blue-500 mr-2 mt-1">•</span>
                            <span>{hint}</span>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Expected Result */}
            {currentChallenge.expected_result && (
              <div className="mb-6">
                <button
                  onClick={() => setExpectedResultCollapsed(!expectedResultCollapsed)}
                  className="w-full flex items-center justify-between text-sm font-medium text-gray-900 mb-2 hover:text-blue-600 transition-colors"
                >
                  <span>Expected Result Format</span>
                  <motion.div
                    animate={{ rotate: expectedResultCollapsed ? -90 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {!expectedResultCollapsed && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-40 font-mono">
                        {JSON.stringify(currentChallenge.expected_result, null, 2)}
                      </pre>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Challenge Info */}
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-blue-700">Time Limit:</span>
                <span className="font-medium text-blue-900">{currentChallenge.time_limit_minutes} minutes</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-blue-700">Database Type:</span>
                <select
                  value={selectedDatabaseType}
                  onChange={(e) => setSelectedDatabaseType(e.target.value)}
                  className="ml-2 px-2 py-1 border border-blue-200 rounded-md text-blue-900 font-medium bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                >
                  {DATABASE_TYPES.map((dbType) => (
                    <option key={dbType.value} value={dbType.value}>
                      {dbType.icon} {dbType.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Vertical Resize Handle */}
      {!problemPanelCollapsed && (
        <div 
          className="w-1 bg-gray-300 hover:bg-blue-500 cursor-col-resize transition-all duration-200 relative group flex items-center justify-center"
          style={{ 
            padding: '0 4px', // Creates 8px total hit area (4px on each side)
            margin: '0 -4px'   // Negative margin to prevent layout shift
          }}
          onMouseDown={(e) => {
            const startX = e.clientX
            const startWidth = leftPanelWidth
            
            const handleMouseMove = (e: MouseEvent) => {
              const deltaX = e.clientX - startX
              const containerWidth = window.innerWidth
              const newWidth = Math.max(35, Math.min(80, startWidth + (deltaX / containerWidth) * 100))
              setLeftPanelWidth(newWidth)
            }
            
            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove)
              document.removeEventListener('mouseup', handleMouseUp)
            }
            
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
          }}
          title="Drag to resize panels"
        >
          {/* Visual indicator dots */}
          <div className="flex flex-col items-center space-y-1 opacity-60 group-hover:opacity-100 transition-opacity">
            <div className="w-0.5 h-0.5 bg-gray-600 group-hover:bg-white rounded-full"></div>
            <div className="w-0.5 h-0.5 bg-gray-600 group-hover:bg-white rounded-full"></div>
            <div className="w-0.5 h-0.5 bg-gray-600 group-hover:bg-white rounded-full"></div>
            <div className="w-0.5 h-0.5 bg-gray-600 group-hover:bg-white rounded-full"></div>
            <div className="w-0.5 h-0.5 bg-gray-600 group-hover:bg-white rounded-full"></div>
          </div>
        </div>
      )}

      {/* SQL Editor Panel */}
      <div 
        className="flex-1 flex flex-col"
      >
        {/* Editor Header */}
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Code2 className="w-5 h-5 mr-2" />
                  SQL Query Editor
                </h3>
                
                {/* Database Type Selector */}
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Database:</label>
                  <select
                    value={selectedDatabaseType}
                    onChange={(e) => setSelectedDatabaseType(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {DATABASE_TYPES.map((dbType) => (
                      <option key={dbType.value} value={dbType.value}>
                        {dbType.icon} {dbType.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <p className="text-sm text-gray-600">
                Write your SQL query using {DATABASE_TYPES.find(db => db.value === selectedDatabaseType)?.label} syntax. 
                {selectedDatabaseType === 'postgresql' && (
                  <span className="text-blue-600"> PostgreSQL supports advanced features like JSONB and window functions.</span>
                )}
                {selectedDatabaseType === 'mysql' && (
                  <span className="text-blue-600"> MySQL uses LIMIT for row limiting and supports auto-increment.</span>
                )}
                {selectedDatabaseType === 'sqlserver' && (
                  <span className="text-blue-600"> SQL Server supports T-SQL syntax with functions like GETDATE(), IIF(), and IDENTITY columns.</span>
                )}
              </p>
            </div>
            
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={handleExecuteQuery}
                  disabled={isRunning || !data || !data.query || isSubmissionLocked}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Play className="w-4 h-4" />
                  <span>{isRunning ? 'Running...' : 'Execute'}</span>
                </button>
                
                {currentChallenge?.expected_result && (
                  <button
                    onClick={handleRunTests}
                    disabled={isRunning || !data || !data.query || isSubmissionLocked}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Target className="w-4 h-4" />
                    <span>{isRunning ? 'Testing...' : 'Run Tests'}</span>
                  </button>
                )}
                
                {!isSubmissionLocked && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowSubmitConfirm(true)}
                      disabled={isSubmitting || !data || !data.query}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>{isSubmitting ? 'Submitting...' : 'Submit'}</span>
                    </button>

                    {showSubmitConfirm && (
                      <div className="flex items-center space-x-3 bg-green-50 border border-green-200 rounded-md px-3 py-2 text-sm text-green-900">
                        <span>Are you sure? You cannot edit after submission.</span>
                        <button
                          onClick={() => {
                            setShowSubmitConfirm(false)
                            void handleSubmitSolution()
                          }}
                          disabled={isSubmitting}
                          className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setShowSubmitConfirm(false)}
                          disabled={isSubmitting}
                          className="px-3 py-1 bg-white text-gray-700 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                )}
              
            </div>
          </div>
        </div>
        
        {/* Code Editor */}
        <div 
          className="relative bg-gray-50"
          style={{ height: `calc(100vh - ${resultsHeight + 200}px)` }}
          onClick={() => {
            // Ensure editor gets focus when container is clicked
            if (editorRef.current) {
              editorRef.current.focus()
            }
          }}
        >
          <Editor
            height="100%"
            defaultLanguage="sql"
            value={data?.query || 'SELECT 1;'}
            onChange={(value) => {
              if (isSubmissionLocked) return
              updateData(value || '')
            }}
            onMount={(editor, monaco) => {
              editorRef.current = editor
              
              // Ensure editor has focus
              editor.focus()
              
              // Make sure editor is properly configured for text editing
              const model = editor.getModel()
              if (model) {
                // Ensure the model is not read-only
                model.updateOptions({ tabSize: 2, insertSpaces: true })
              }
            }}
            theme="vs-light"
            options={{
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              fontSize: 14,
              wordWrap: 'on',
              automaticLayout: true,
              lineNumbers: 'on',
              folding: true,
              readOnly: isSubmissionLocked,
              formatOnPaste: true,
              formatOnType: true,
              acceptSuggestionOnEnter: 'on',
              acceptSuggestionOnCommitCharacter: true,
              selectOnLineNumbers: true,
              roundedSelection: false,
              cursorStyle: 'line',
              cursorBlinking: 'blink',
              overviewRulerLanes: 0,
              hideCursorInOverviewRuler: true,
              scrollbar: {
                vertical: 'visible',
                horizontal: 'visible'
              }
            }}
          />
        </div>

        {/* Resize Handle */}
        <div 
          className={`bg-gray-300 hover:bg-blue-400 cursor-row-resize flex items-center justify-center transition-colors duration-200 relative ${isResizing ? 'bg-blue-500' : ''}`}
          style={{ height: '8px' }}
          onMouseDown={handleResizeStart}
          title="Drag to resize results panel"
        >
          <div className="flex space-x-1">
            <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
            <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
            <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
            <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
            <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
          </div>
          <div className="absolute inset-x-0 top-0 h-full bg-transparent hover:bg-blue-200 opacity-0 hover:opacity-30 transition-opacity"></div>
        </div>

        {/* Results Panel - Resizable */}
        <div 
          className="bg-white border-t border-gray-200 p-6 overflow-auto"
          style={{ height: `${resultsHeight}px` }}
        >
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center">
              <Terminal className="w-5 h-5 mr-2 text-blue-600" />
              Results
            </h4>
          </div>
          
          {!output && !executionResult && !isRunning && (
            <div className="text-center py-12 text-gray-500">
              <Play className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-lg font-medium">Ready to execute your query</p>
              <p className="text-sm mt-2">Click the "Run Query" button to see results here</p>
            </div>
          )}

          {isRunning && (
            <div className="text-center py-12 text-blue-600">
              <div className="animate-spin w-16 h-16 mx-auto border-4 border-blue-200 border-t-blue-600 rounded-full mb-4"></div>
              <p className="text-lg font-medium">Executing query...</p>
              <p className="text-sm mt-2">Please wait while your SQL query is processed</p>
            </div>
          )}
          
            
            {executionResult && (
              <div className="space-y-4">

                {/* Query Results Table */}
                {executionResult.rows && executionResult.rows.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <TableProperties className="w-5 h-5 mr-2 text-green-600" />
                        <span className="text-base font-semibold text-gray-900">
                          Query Results ({executionResult.rows.length} rows)
                        </span>
                      </div>
                      {executionResult.execution_time_ms && (
                        <div className="flex items-center text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                          <Clock className="w-4 h-4 mr-1" />
                          {Math.round(executionResult.execution_time_ms)} ms
                        </div>
                      )}
                    </div>
                    <div 
                      className="overflow-auto border-2 border-gray-200 rounded-lg shadow-sm"
                      style={{ 
                        maxHeight: `${resultsHeight - 160}px`,
                        maxWidth: '100%'
                      }}
                    >
                      <div className="overflow-x-auto overflow-y-auto">
                        <table className="text-sm" style={{ minWidth: 'max-content' }}>
                          <thead className="bg-blue-50 sticky top-0">
                            <tr>
                              {executionResult.columns?.map((col: string, i: number) => (
                                <th 
                                  key={i} 
                                  className="border-b-2 border-blue-200 px-4 py-3 text-left font-semibold text-blue-900 whitespace-nowrap"
                                  style={{ 
                                    minWidth: '120px',
                                    maxWidth: '300px'
                                  }}
                                >
                                  <div 
                                    className="truncate" 
                                    title={col}
                                    style={{ maxWidth: '280px' }}
                                  >
                                    {col}
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white">
                            {executionResult.rows?.map((row: any, i: number) => {
                              // Handle both array and object row formats
                              const rowValues = Array.isArray(row) 
                                ? row 
                                : executionResult.columns?.map((col: string) => row[col]) || Object.values(row);
                              
                              return (
                                <tr key={i} className={`border-b border-gray-200 hover:bg-gray-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                                  {rowValues.map((cell: any, j: number) => (
                                    <td 
                                      key={j} 
                                      className="border-r border-gray-200 px-4 py-3 text-gray-700"
                                      style={{ 
                                        minWidth: '120px',
                                        maxWidth: '300px'
                                      }}
                                    >
                                      {cell === null ? (
                                        <span className="text-gray-400 italic font-mono">NULL</span>
                                      ) : (
                                        <div 
                                          className="font-mono truncate" 
                                          title={String(cell)}
                                          style={{ maxWidth: '280px' }}
                                        >
                                          {String(cell)}
                                        </div>
                                      )}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Empty Results Message */}
                {executionResult && executionResult.rows && executionResult.rows.length === 0 && (
                  <div className="mt-6 text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                    <Database className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-600 font-medium">No rows returned</p>
                    <p className="text-sm text-gray-500 mt-1">Your query executed successfully but returned no data</p>
                  </div>
                )}

                {/* Test Results Section */}
                {displayedValidationResult && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <Target
                        className={`w-5 h-5 mr-2 ${
                          validationStatus?.tone === 'success'
                            ? 'text-green-600'
                            : validationStatus?.tone === 'warning'
                              ? 'text-yellow-600'
                              : 'text-red-600'
                        }`}
                      />
                      <span className="text-base font-semibold text-gray-900">
                        Test Results
                      </span>
                    </div>
                    {validationStatus && (
                      <div className={`flex items-center text-sm px-3 py-1 rounded-full font-medium ${validationStatus.badgeClass}`}>
                        <validationStatus.icon className={`w-4 h-4 mr-1 ${validationStatus.iconClass}`} />
                        {validationStatus.label}
                      </div>
                    )}
                  </div>

                    {/* Test Feedback */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
                        <div>
                          <span className="text-sm font-medium text-gray-700">Test Status:</span>
                          <span className={`ml-2 font-medium ${validationStatus?.statusClass || 'text-gray-700'}`}>
                            {validationStatus?.statusText || 'N/A'}
                          </span>
                        </div>
                        {typeof displayedValidationResult.execution_time_ms === 'number' && (
                          <div>
                            <span className="text-sm font-medium text-gray-700">Execution Time:</span>
                            <span className="ml-2 font-medium text-gray-900">
                              {displayedValidationResult.execution_time_ms} ms
                            </span>
                          </div>
                        )}
                      </div>
                      {displayedValidationResult.feedback && (
                        <div className="text-sm text-gray-700 bg-white border border-gray-200 rounded p-3">
                          <span className="font-medium">Feedback:</span> {displayedValidationResult.feedback}
                        </div>
                      )}
                    </div>

                    {displayedValidationResult.comparison_details && displayedValidationResult.comparison_details.total_checks > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="border border-green-200 rounded-lg bg-green-50 p-3">
                          <h4 className="text-sm font-semibold text-green-800 mb-2">
                            Passed Checks ({displayedValidationResult.comparison_details.success_count})
                          </h4>
                          {displayedValidationResult.comparison_details.successful_checks.length > 0 ? (
                            <ul className="space-y-2 text-sm text-green-700">
                              {displayedValidationResult.comparison_details.successful_checks.map((check, index) => (
                                <li key={`${check.type}-${index}`} className="flex items-start">
                                  <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-green-600 flex-shrink-0" />
                                  <span>{check.message}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-green-700">No checks passed yet.</p>
                          )}
                        </div>
                        <div className="border border-yellow-200 rounded-lg bg-yellow-50 p-3">
                          <h4 className="text-sm font-semibold text-yellow-800 mb-2">
                            Needs Attention ({displayedValidationResult.comparison_details.failure_count})
                          </h4>
                          {displayedValidationResult.comparison_details.failed_checks.length > 0 ? (
                            <ul className="space-y-2 text-sm text-yellow-700">
                              {displayedValidationResult.comparison_details.failed_checks.map((check, index) => (
                                <li key={`${check.type}-${index}`} className="flex items-start">
                                  <AlertCircle className="w-4 h-4 mr-2 mt-0.5 text-yellow-600 flex-shrink-0" />
                                  <span>{check.message}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-yellow-700">Great job! No issues found.</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Detailed Differences */}
                    {displayedValidationResult.differences && Object.keys(displayedValidationResult.differences).length > 0 && (
                      <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                        <h4 className="text-sm font-semibold text-red-800 mb-3 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          Differences Found
                        </h4>
                        
                        {displayedValidationResult.difference_messages && displayedValidationResult.difference_messages.length > 0 && (
                          <div className="mb-4">
                            <p className="text-sm font-medium text-red-700 mb-2">Issues:</p>
                            <ul className="text-sm text-red-700 space-y-1">
                              {displayedValidationResult.difference_messages.map((message: string, index: number) => (
                                <li key={index} className="flex items-start">
                                  <span className="text-red-500 mr-2 mt-1">•</span>
                                  <span>{message}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Row count mismatch */}
                        {displayedValidationResult.differences.row_count && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-red-700">Row Count Mismatch:</p>
                            <p className="text-sm text-red-600">
                              Expected: {displayedValidationResult.differences.row_count.expected} rows, 
                              Got: {displayedValidationResult.differences.row_count.actual} rows
                            </p>
                          </div>
                        )}

                        {/* Missing rows */}
                        {displayedValidationResult.differences.missing_rows && displayedValidationResult.differences.missing_rows.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-red-700">Missing Rows:</p>
                            <div className="bg-white border border-red-200 rounded p-2 max-h-32 overflow-auto">
                              <pre className="text-xs text-red-600 font-mono">
                                {JSON.stringify(displayedValidationResult.differences.missing_rows, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}

                        {/* Extra rows */}
                        {displayedValidationResult.differences.unexpected_rows && displayedValidationResult.differences.unexpected_rows.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-red-700">Unexpected Rows:</p>
                            <div className="bg-white border border-red-200 rounded p-2 max-h-32 overflow-auto">
                              <pre className="text-xs text-red-600 font-mono">
                                {JSON.stringify(displayedValidationResult.differences.unexpected_rows, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}

                        {/* Column differences */}
                        {(displayedValidationResult.differences.missing_columns || displayedValidationResult.differences.unexpected_columns) && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-red-700">Column Issues:</p>
                            {displayedValidationResult.differences.missing_columns && (
                              <p className="text-sm text-red-600">
                                Missing columns: {displayedValidationResult.differences.missing_columns.join(', ')}
                              </p>
                            )}
                            {displayedValidationResult.differences.unexpected_columns && (
                              <p className="text-sm text-red-600">
                                Unexpected columns: {displayedValidationResult.differences.unexpected_columns.join(', ')}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Expected vs Actual Results Comparison */}
                    {displayedValidationResult.expected_result && displayedValidationResult.user_result && (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Expected Results */}
                        <div className="border border-gray-200 rounded-lg">
                          <div className="bg-green-50 border-b border-green-200 px-3 py-2">
                            <h5 className="text-sm font-semibold text-green-800">Expected Result</h5>
                          </div>
                          <div className="p-3 max-h-48 overflow-auto">
                            {displayedValidationResult.expected_result.rows && displayedValidationResult.expected_result.rows.length > 0 ? (
                              <div className="overflow-x-auto overflow-y-auto max-w-full">
                                <table className="text-xs" style={{ minWidth: 'max-content' }}>
                                  <thead className="bg-green-50">
                                    <tr>
                                      {displayedValidationResult.expected_result.columns?.map((col: string, i: number) => (
                                        <th 
                                          key={i} 
                                          className="border border-green-200 px-2 py-1 text-left font-semibold text-green-900 whitespace-nowrap"
                                          style={{ 
                                            minWidth: '80px',
                                            maxWidth: '200px'
                                          }}
                                        >
                                          <div 
                                            className="truncate" 
                                            title={col}
                                            style={{ maxWidth: '180px' }}
                                          >
                                            {col}
                                          </div>
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {displayedValidationResult.expected_result.rows.map((row: any[], i: number) => (
                                      <tr key={i} className="border-b border-green-100">
                                        {row.map((cell: any, j: number) => (
                                          <td 
                                            key={j} 
                                            className="border border-green-200 px-2 py-1 text-green-700 font-mono text-xs"
                                            style={{ 
                                              minWidth: '80px',
                                              maxWidth: '200px'
                                            }}
                                          >
                                            {cell === null ? (
                                              <span className="text-gray-400 italic">NULL</span>
                                            ) : (
                                              <div 
                                                className="truncate" 
                                                title={String(cell)}
                                                style={{ maxWidth: '180px' }}
                                              >
                                                {String(cell)}
                                              </div>
                                            )}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 italic">No expected rows</p>
                            )}
                          </div>
                        </div>

                        {/* Actual Results */}
                        <div className="border border-gray-200 rounded-lg">
                          <div className="bg-blue-50 border-b border-blue-200 px-3 py-2">
                            <h5 className="text-sm font-semibold text-blue-800">Your Result</h5>
                          </div>
                          <div className="p-3 max-h-48 overflow-auto">
                            {displayedValidationResult.user_result.rows && displayedValidationResult.user_result.rows.length > 0 ? (
                              <div className="overflow-x-auto overflow-y-auto max-w-full">
                                <table className="text-xs" style={{ minWidth: 'max-content' }}>
                                  <thead className="bg-blue-50">
                                    <tr>
                                      {displayedValidationResult.user_result.columns?.map((col: string, i: number) => (
                                        <th 
                                          key={i} 
                                          className="border border-blue-200 px-2 py-1 text-left font-semibold text-blue-900 whitespace-nowrap"
                                          style={{ 
                                            minWidth: '80px',
                                            maxWidth: '200px'
                                          }}
                                        >
                                          <div 
                                            className="truncate" 
                                            title={col}
                                            style={{ maxWidth: '180px' }}
                                          >
                                            {col}
                                          </div>
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {displayedValidationResult.user_result.rows.map((row: any[], i: number) => (
                                      <tr key={i} className="border-b border-blue-100">
                                        {row.map((cell: any, j: number) => (
                                          <td 
                                            key={j} 
                                            className="border border-blue-200 px-2 py-1 text-blue-700 font-mono text-xs"
                                            style={{ 
                                              minWidth: '80px',
                                              maxWidth: '200px'
                                            }}
                                          >
                                            {cell === null ? (
                                              <span className="text-gray-400 italic">NULL</span>
                                            ) : (
                                              <div 
                                                className="truncate" 
                                                title={String(cell)}
                                                style={{ maxWidth: '180px' }}
                                              >
                                                {String(cell)}
                                              </div>
                                            )}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 italic">No rows returned</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
    </div>
  )
}
